import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, BookOpen, ArrowLeft } from 'lucide-react';
import oneSpaceApi from '../lib/onespace-api';

interface KnowledgeSearchResult {
  slug: string;
  title: string;
  excerpt: string;
  url: string;
  relevance: number;
}

const KnowledgeSearch: React.FC = () => {
  // Haal de zoekparameter uit de URL
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  
  // State voor de zoekresultaten
  const [results, setResults] = useState<KnowledgeSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(query);
  
  // Zoekfunctie
  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (searchTerm.trim()) {
      setSearchParams({ q: searchTerm.trim() });
    }
  };
  
  // Zoek wanneer de query parameter verandert
  useEffect(() => {
    const fetchResults = async () => {
      if (!query) {
        setResults([]);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await oneSpaceApi.get<{ results: KnowledgeSearchResult[] }>(`/api/knowledge/search?query=${encodeURIComponent(query)}`);
        setResults(response.data.results);
      } catch (err) {
        console.error('Error searching knowledge base:', err);
        setError('De zoekresultaten konden niet worden geladen. Probeer het later opnieuw.');
        
        // Voor demo/ontwikkeling: genereer dummy resultaten
        if (process.env.NODE_ENV === 'development') {
          const dummyResults: KnowledgeSearchResult[] = [];
          
          for (let i = 1; i <= 5; i++) {
            dummyResults.push({
              slug: `article-${i}-${query.replace(/\s+/g, '-').toLowerCase()}`,
              title: `KB artikel ${i}: ${query}`,
              excerpt: `Dit is een dummy knowledgebase resultaat voor zoekterm "${query}". Dit is resultaat nummer ${i}.`,
              url: `/k/${`article-${i}-${query.replace(/\s+/g, '-').toLowerCase()}`}`,
              relevance: (5 - i + 1) / 5
            });
          }
          
          setResults(dummyResults);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchResults();
  }, [query]);
  
  // Render een resultaatkaart
  const renderResultCard = (result: KnowledgeSearchResult) => (
    <Card key={result.slug} className="mb-4 hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">
          <Link to={`/k/${result.slug}`} className="text-primary hover:underline">
            {result.title}
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">{result.excerpt}</p>
        <div className="mt-2">
          <Button 
            variant="link" 
            size="sm" 
            className="p-0 h-auto text-primary"
            asChild
          >
            <Link to={`/k/${result.slug}`}>Lees meer</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
  
  return (
    <div className="container mx-auto px-4 py-8">
      <Button
        variant="outline"
        size="sm"
        className="mb-4 flex items-center gap-2"
        onClick={() => window.history.back()}
      >
        <ArrowLeft className="h-4 w-4" />
        Terug
      </Button>
      
      <div className="flex items-center gap-2 mb-6">
        <BookOpen className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">Knowledgebase Zoeken</h1>
      </div>
      
      <Card className="mb-8">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Zoek in de kennisbank..."
              className="flex-1"
            />
            <Button type="submit">
              <Search className="h-4 w-4 mr-2" />
              Zoeken
            </Button>
          </form>
        </CardContent>
      </Card>
      
      {query && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold mb-2">
            Zoekresultaten voor: <span className="text-primary">"{query}"</span>
          </h2>
          <p className="text-muted-foreground">
            {results.length} resultaten gevonden
          </p>
        </div>
      )}
      
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <Card className="w-full border border-red-200 bg-red-50 text-red-800">
          <CardContent className="pt-6">
            <p>{error}</p>
          </CardContent>
        </Card>
      ) : results.length > 0 ? (
        <div>
          {results.map(renderResultCard)}
        </div>
      ) : query ? (
        <Card className="w-full">
          <CardContent className="pt-6">
            <p>Geen resultaten gevonden voor "{query}".</p>
            <p className="text-muted-foreground mt-2">Probeer andere zoektermen of controleer je spelling.</p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default KnowledgeSearch; 